import { useEffect, useState } from "react";
import { Space, Button, Row, Col, Input, Drawer, Select, Form, notification } from "antd";
import {
  useCreateUserMutation,
  useGetUserByIdQuery,
  useUpdateUserMutation,
  useGetUsersQuery
} from "../../redux/services/userApi";
import ErrorDetails from "../../components/ErrorDetails";
import { useGetBrandsQuery } from "../../redux/services/brandApi";

const { Option } = Select;

const UserForm = (props: any) => {
  const { isOpen, setOpen, userId, refetch, isManager } = props;

  const [api, contextHolder] = notification.useNotification();
  const [form] = Form.useForm();

  const { data: userData, isLoading } = useGetUserByIdQuery(userId);
  const selectedUser = userData?.user;

  const [createUser, { isLoading: createInProgress }] = useCreateUserMutation();

  const [updateUser, { isLoading: updateInProgress }] = useUpdateUserMutation();

  const { data: usersData } = useGetUsersQuery({ all_pages: true, user_type: ["ecb"] });

  const { data: brandsData } = useGetBrandsQuery({ all_pages: true });

  const brands = brandsData?.brands || [];
  const ecbUsers = usersData?.users;

  const [userType, setUserType] = useState(null);

  useEffect(() => {
    setUserType(selectedUser?.user_type);
  }, [selectedUser?.user_type]);

  const onFinish = (values: any) => {
    const payload = {
      ...values,
      supervisor_id: values.supervisor_id ?? null
    };

    if (userId) {
      onUpdateUser(payload);
    } else {
      addUser(payload);
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log("Failed:", errorInfo);
  };

  const addUser = async (values: object) => {
    try {
      await createUser({ user: values }).unwrap();
      api.success({
        message: "Create user successful",
        placement: "top"
      });
      form.resetFields();
      setOpen(false);
      refetch();
    } catch (error: any) {
      api.info({
        message: "Create user failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
    }
  };

  const onUpdateUser = async (values: object) => {
    try {
      await updateUser({ user: { id: userId, ...values } }).unwrap();
      api.success({
        message: "Update user successful",
        placement: "top"
      });
      form.resetFields();
      setOpen(false);
      refetch();
    } catch (error: any) {
      api.info({
        message: "Update user failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
    }
  };

  useEffect(() => {
    if (selectedUser) {
      form.setFieldsValue({
        ...selectedUser
      });
    }
    return () => {
      form.resetFields();
    };
  }, [selectedUser, userId]);

  return (
    <>
      {contextHolder}
      <Drawer
        title={`${userId ? "Update" : "Add"} User`}
        placement="right"
        closable={true}
        onClose={() => {
          form.resetFields();
          setOpen(!isOpen);
        }}
        open={isOpen}
        key={"user-drawer"}
      >
        <Form
          name="user_form"
          form={form}
          initialValues={{ remember: true }}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          autoComplete="off"
          disabled={updateInProgress || createInProgress || isLoading}
        >
          <Form.Item
            label="Name"
            name="name"
            rules={[
              { required: true, message: "Please input name" },
              { min: 3, message: "Name must be minimum 3 characters." },
              { max: 30, message: "Name must be maximum 30 characters." }
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: "Please input email" },
              { min: 4, message: "Email must be minimum 4 characters." },
              { max: 100, message: "Email must be maximum 100 characters." }
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Password"
            name="password"
            rules={[
              { required: !userId, message: "Please input Password" },
              { min: 6, message: "Password must be minimum 6 characters." },
              { max: 128, message: "Password must be maximum 128 characters." }
            ]}
          >
            <Input.Password />
          </Form.Item>

          <Form.Item
            label="Confirm Password"
            name="password_confirmation"
            rules={[
              { required: !userId, message: "Please input Confirm Password" },
              { min: 6, message: "Confirm Password must be minimum 6 characters." },
              { max: 128, message: "Confirm Password must be maximum 128 characters." }
            ]}
          >
            <Input.Password />
          </Form.Item>

          <Form.Item
            label="Mobile No."
            name="mobile"
            rules={[
              { required: true, message: "Please input mobile number" },
              { min: 10, message: "Mobile No. must be minimum 10 characters." },
              { max: 10, message: "Mobile No. be maximum 10 characters." },
              { pattern: /^[0-9]+$/, message: "Mobile No. must contain only numbers." }
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Another Contact Number"
            name="alternate_mobile"
            rules={[
              { min: 10, message: "Another Contact Number must be minimum 10 digit." },
              { max: 10, message: "Another Contact Number be maximum 10 digit." },
              { pattern: /^[0-9]+$/, message: "Contact Number must contain only numbers." }
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: "Please select status" }]}
          >
            <Select
              placeholder="Select status"
              showSearch
              optionFilterProp="children"
              filterOption={(input, option: any) =>
                option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
              <Option value="disabled">Disabled</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Aadhar Number"
            name="aadhaar"
            rules={[
              { min: 12, message: "Aadhar Number must be minimum 12 digit." },
              { max: 12, message: "Aadhar Number be maximum 12 digit." }
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Employee ID"
            name="employee_id"
            rules={[
              { min: 2, message: "Employee ID must be minimum 2 digit." },
              { max: 20, message: "Employee ID be maximum 20 digit." }
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="supervisor_id" label="Select Supervisor">
            <Select
              allowClear
              placeholder="Select Supervisor"
              showSearch
              optionFilterProp="children"
              filterOption={(input, option: any) =>
                option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {ecbUsers?.map((ecbUser: any) => (
                <Option key={ecbUser?.name} value={ecbUser?.id}>
                  {ecbUser?.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="user_type"
            label="User Type"
            rules={[{ required: true, message: "Please select user type" }]}
          >
            <Select
              placeholder="Select user type"
              showSearch
              optionFilterProp="children"
              onSelect={(value) => setUserType(value)}
              filterOption={(input, option: any) =>
                option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {!isManager && <Option value="ecb">eCB</Option>}
              <Option value="external">External</Option>
              {!isManager && <Option value="client">Client</Option>}
            </Select>
          </Form.Item>

          {userType === "client" && (
            <Form.Item
              name="brand_id"
              label="Brand"
              rules={[{ required: true, message: "Please select brand" }]}
            >
              <Select
                placeholder="Select brand"
                allowClear
                showSearch
                optionFilterProp="children"
                filterOption={(input, option: any) =>
                  option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {brands?.map((brand: any) => (
                  <Option key={brand?.name} value={brand?.id}>
                    {brand?.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item wrapperCol={{ offset: 0, span: 24 }}>
            <Row>
              <Col>
                <Space size={"middle"}>
                  <Button
                    type="default"
                    onClick={() => {
                      form.resetFields();
                      setOpen(!isOpen);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={updateInProgress || createInProgress}
                  >
                    {userId ? "Update" : "Add"}
                  </Button>
                </Space>
              </Col>
            </Row>
          </Form.Item>
        </Form>
      </Drawer>
    </>
  );
};

export default UserForm;
